import React, { useCallback, useEffect, useRef, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import { verdictBadge, roleLabel } from '../../utils/distributionView';

const btn = 'px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40';

function Badge({ view }) {
  const b = verdictBadge(view);
  if (!b) return null;
  return <span title={view.summary || ''} className={`px-1.5 py-0.5 rounded text-xs font-medium ${b.className}`}>{b.text}</span>;
}

const feeAmount = (v) => `$${Number(v.placementFee).toFixed(2)}`;
const fee = (v) => ((v.placementFee == null || v.placementFee === 0) ? 'free' : `${feeAmount(v)} fee`);
const FEE_WAIVED_AT_BOXES = 5;

/** Options for one ASIN: Best distribution first, then live draws scored Better / No change / Worse. */
export default function OptionsPanel({ line, roles, onConfirmed, refreshKey }) {
  const asin = line.asin;
  const [data, setData] = useState({ groups: [], suggestions: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [confirmErrors, setConfirmErrors] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const alive = useRef(true);
  const seq = useRef(0);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true); setLoadError(null);
    try {
      const units = (line.depthQty || 0) + (line.spreadQty || 0);
      const [c, s] = await Promise.all([
        ApiClient.get(`/api/shipment-planner/placement-candidates/${encodeURIComponent(asin)}`),
        ApiClient.get(`/api/shipment-planner/placement-suggestions/${encodeURIComponent(asin)}?units=${units}&includeDistribution=true`),
      ]);
      if (!alive.current || id !== seq.current) return;
      const sugg = Array.isArray(s.data) ? s.data : (s.data?.suggestions || []);
      setData({ groups: c.data?.groups || [], suggestions: sugg });
    } catch (e) {
      if (alive.current && id === seq.current) setLoadError('Could not load options');
    } finally {
      if (alive.current && id === seq.current) setLoading(false);
    }
  }, [asin, line.depthQty, line.spreadQty]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Rows: the smallest variant of each live group.
  const rows = data.groups.filter((g) => g.anyLive).map((g) => {
    const variants = (g.variants || []).filter((v) => !v.expired).sort((a, b) => (a.units || 0) - (b.units || 0));
    return { g, v: variants[0] };
  }).filter((r) => r.v).sort((a, b) => {
    // Ruling I4 (D4): free-or-fee-justified above unjustified-fee regardless of improvement;
    // then improving before non-improving; then score, fee, units.
    const ok = (r) => ((r.v.placementFee || 0) <= 0 || r.g.distribution?.feeJustified ? 1 : 0);
    if (ok(a) !== ok(b)) return ok(b) - ok(a);
    const ia = (a.g.distribution?.score || 0) > 0 ? 1 : 0; const ib = (b.g.distribution?.score || 0) > 0 ? 1 : 0;
    if (ia !== ib) return ib - ia;
    const d = (b.g.distribution?.score || 0) - (a.g.distribution?.score || 0);
    if (d) return d;
    const fd = (a.v.placementFee || 0) - (b.v.placementFee || 0);
    if (fd) return fd;
    return (a.v.units || 0) - (b.v.units || 0);
  });
  const best = data.suggestions.find((s) => s.key === 'BEST_DISTRIBUTION');

  // Owner ruling (amended): placement fees are a shipment-size effect — Amazon waives them at 5+ boxes.
  // Informational only: never changes ranking or picking. Unknown packaging = no note.
  const perBox = Number(line.pkg?.unitsPerBox) || 0;
  const drawUnits = (line.depthQty || 0) + (line.spreadQty || 0);
  const boxes = perBox > 0 && drawUnits > 0 ? Math.ceil(drawUnits / perBox) : null;
  const top = rows[0];
  const feeBoxNote = boxes != null && boxes < FEE_WAIVED_AT_BOXES && top && (top.v.placementFee || 0) > 0
    ? `${boxes} boxes — placement fee ${feeAmount(top.v)}. Amazon waives the fee at ${FEE_WAIVED_AT_BOXES} or more boxes (${FEE_WAIVED_AT_BOXES - boxes} more).`
    : null;

  // One plan per click; same wording and endpoint as PlacementWorkspace.confirmPlan.
  const confirmPlan = async (p) => {
    if (p?.expired || !p?.inboundPlanId || !p?.placementOptionId) return;
    if (!window.confirm(
      `Confirm run ${p.runId} — ${p.units} units to ${(p.fcs || []).length} FCs — with Amazon?\n\n`
      + 'This locks the placement, books Amazon partnered transport, and cannot be undone.'
    )) return;
    setConfirmingId(p.runId);
    setConfirmErrors((prev) => ({ ...prev, [p.runId]: null }));
    try {
      await ApiClient.post('/api/shipment-planner/confirm', {
        planId: p.inboundPlanId, placementOptionId: p.placementOptionId, acknowledgeUsedCondition: false,
      }, { timeout: 120000 });
      if (alive.current) setNotice(`Run ${p.runId} confirmed`);
      if (onConfirmed) await onConfirmed(asin);
      if (alive.current) await load();
    } catch (err) {
      if (alive.current) {
        setConfirmErrors((prev) => ({
          ...prev,
          [p.runId]: err.response?.data?.error || err.message || 'Confirm failed — the plan was NOT sent to Amazon.',
        }));
      }
    }
    if (alive.current) setConfirmingId(null);
  };

  const confirmable = (g, v) => ({ ...v, fcs: v.fcs || g.fcs || [] });

  if (loading && !rows.length && !best) return <div className="text-sm text-gray-500 dark:text-gray-400">Loading options…</div>;
  if (loadError) return <div className="text-sm text-red-600 dark:text-red-400">{loadError}</div>;

  return (
    <div className="space-y-2 text-sm" data-testid={`options-${asin}`}>
      {notice && <div className="text-green-700 dark:text-green-400">{notice}</div>}
      {best && (
        <div className="rounded border border-green-300 dark:border-green-700 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{best.label || 'Best distribution'}</span>
            <Badge view={best.distribution} />
            {best.distribution?.summary && <span className="text-gray-600 dark:text-gray-300">{best.distribution.summary}</span>}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Plans: {(best.runIds || []).map((r) => `#${r}`).join(', ') || '—'}{best.rationale ? ` — ${best.rationale}` : ''}
          </div>
        </div>
      )}
      {feeBoxNote && <div data-testid="fee-box-note" className="text-xs text-amber-700 dark:text-amber-400">{feeBoxNote}</div>}
      {!rows.length && <div className="text-gray-500 dark:text-gray-400">No live draws for this product yet.</div>}
      {rows.map(({ g, v }) => {
        const key = v.runId ?? (g.fcs || []).join('-');
        const cand = confirmable(g, v);
        return (
          <div key={key} className="flex flex-wrap items-center gap-2 rounded border border-gray-200 dark:border-gray-700 p-2">
            <div className="flex flex-wrap gap-2">
              {(g.fcs || []).map((fc) => (
                <span key={fc} className="font-mono text-xs">{fc} <span className="text-gray-500 dark:text-gray-400">{roleLabel(roles?.[fc]?.role, roles?.[fc])}</span></span>
              ))}
            </div>
            <span>{v.units} units</span>
            <span>{fee(v)}</span>
            <Badge view={g.distribution} />
            {(g.distribution?.reachesSpeedLoss || []).length > 0 && (
              <span className="text-xs text-amber-700 dark:text-amber-300">
                reaches losing region: {g.distribution.reachesSpeedLoss.join(', ')}
              </span>
            )}
            {g.marginalGain1Day != null && (
              <span className="text-xs text-gray-400">1-day reach +{Number(g.marginalGain1Day).toFixed(1)}</span>
            )}
            <button type="button" className={btn} disabled={confirmingId != null || v.expired || !v.inboundPlanId || !v.placementOptionId}
              onClick={() => confirmPlan(cand)}>
              {confirmingId === v.runId ? 'Confirming…' : 'Confirm'}
            </button>
            {confirmErrors[v.runId] && <span className="text-xs text-red-600 dark:text-red-400">{confirmErrors[v.runId]}</span>}
          </div>
        );
      })}
    </div>
  );
}
